import { DataType } from "../types/types";

const DB_NAME = "windtransfer";
const STORE_NAME = "form_data";
const DB_VERSION = 1;
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24h

interface StoredForm {
  folderId: string;
  data: DataType;
  currentPage: number;
  savedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "folderId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveFormData(
  folderId: string,
  data: DataType,
  currentPage: number
): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const entry: StoredForm = {
      folderId,
      data,
      currentPage,
      savedAt: Date.now(),
    };

    store.put(entry);
    tx.oncomplete = () => db.close();
    tx.onerror = () => db.close();
  } catch (e) {
    console.error("[FormStorage] saveFormData exception:", e);
  }
}

export async function loadFormData(
  folderId: string
): Promise<{ data: DataType; currentPage: number } | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.get(folderId);

      request.onsuccess = () => {
        db.close();
        const entry: StoredForm | undefined = request.result;

        if (!entry) {
          resolve(null);
          return;
        }

        if (Date.now() - entry.savedAt > EXPIRY_MS) {
          deleteFormData(folderId);
          resolve(null);
          return;
        }

        resolve({ data: entry.data, currentPage: entry.currentPage });
      };

      request.onerror = () => {
        db.close();
        resolve(null);
      };
    });
  } catch (e) {
    console.error("[FormStorage] loadFormData exception:", e);
    return null;
  }
}

export async function deleteFormData(folderId: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete(folderId);
    tx.oncomplete = () => db.close();
  } catch (e) {
    console.error("[FormStorage] deleteFormData exception:", e);
  }
}
